import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useWorkspaceActiveCustomFields } from "@web-app/api";
import { Button, Drawer, Input, Modal, useAlert } from "@web-app/components";
import {
  addChart,
  deleteChart,
  executeSQLQuery,
  transformCubeDataToChartFormat,
  updateChart,
} from "@web-app/services/analytics";
import type {
  BasicChartData,
  Chart,
  ChartDataUnion,
  ChartType,
  CreateChartPayload,
  CubeQueryConfig,
  CubeQueryResponse,
  QueryType,
  SQLQueryConfig,
  TableData,
} from "@web-app/types";

import DateRangeDropdown from "../DateRangeDropdown";
import { dateOnlyToLocalDate } from "../dateUtils";
import {
  getTransformableCustomFieldSlugs,
  transformSQLQuery as transformSQLQueryWithSlugs,
} from "../utils/customFieldQuery";

import ChartRenderer from "./ChartRenderer";
import GUIBuilder from "./GUIBuilder";
import SQLEditor from "./SQLEditor";
import SchemaReference from "./SchemaReference";

// Chart type labels and metadata
const CHART_TYPE_LABELS: Partial<Record<ChartType, string>> = {
  area: "Area Chart",
  bar: "Bar Chart",
  column: "Column Chart",
  donut: "Donut Chart",
  gauge: "Gauge",
  line: "Line Chart",
  metric: "Metric Card",
  pie: "Pie Chart",
  table: "Table",
};

// Common chart types for SQL queries
const COMMON_SQL_CHART_TYPES: ChartType[] = [
  "bar",
  "column",
  "line",
  "area",
  "pie",
  "donut",

  "metric",
  "table",
];

// All other chart types
const ADVANCED_SQL_CHART_TYPES: ChartType[] = ["gauge"];

interface ChartBuilderProps {
  chart?: Chart; // Optional - if provided, we're editing
  charts: Chart[]; // All existing charts for calculating new positions
  dashboardDateRange: { from: string; to: string };
  dashboardId: string;
  isOpen: boolean;
  onChartCreated: () => void;
  onClose: () => void;
}

// Chart types that support X/Y axes
const CHART_TYPES_WITH_AXES: ChartType[] = ["bar", "column", "line", "area"];

export default function ChartBuilder({
  chart,
  charts,
  isOpen,
  onClose,
  dashboardId,
  dashboardDateRange,
  onChartCreated,
}: ChartBuilderProps) {
  const isEditMode = !!chart;
  const { showAlert } = useAlert();

  // Fetch custom fields for SQL transformation
  const { customFields } = useWorkspaceActiveCustomFields();

  // Get list of custom field slugs for transformation
  const customFieldSlugs = useMemo(
    () => getTransformableCustomFieldSlugs(customFields || []),
    [customFields],
  );

  // Transform SQL query to replace custom field slugs with fully qualified names
  const transformSQLQuery = useCallback(
    (sql: string): string => transformSQLQueryWithSlugs(sql, customFieldSlugs),
    [customFieldSlugs],
  );

  // Query type selection (tracks which tab is active)
  const [activeTab, setActiveTab] = useState<number>(0); // 0 = GUI, 1 = SQL
  const queryType: QueryType = activeTab === 0 ? "cube" : "sql";

  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Chart configuration
  const [chartTitle, setChartTitle] = useState("");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [xAxisLabel, setXAxisLabel] = useState("");
  const [yAxisLabel, setYAxisLabel] = useState("");
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({});

  // GUI Builder state
  const [guiQuery, setGuiQuery] = useState<CubeQueryConfig | null>(null);
  const [guiAutoTitle, setGuiAutoTitle] = useState("");

  // SQL Query configuration
  const [sqlQuery, setSqlQuery] = useState<string>("");
  const insertTextRef = useRef<((text: string) => void) | null>(null);

  // Insert text at cursor in SQL editor (for schema reference)
  const handleInsertText = useCallback((text: string) => {
    if (insertTextRef.current) {
      insertTextRef.current(text + " ");
    }
  }, []);

  // Callback to register the insert function from SQLEditor
  const handleRegisterInsert = useCallback(
    (insertFn: (text: string) => void) => {
      insertTextRef.current = insertFn;
    },
    [],
  );

  // Preview state
  const [previewData, setPreviewData] = useState<ChartDataUnion | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDateRange, setPreviewDateRange] = useState(dashboardDateRange);

  // Initialize form when editing
  useEffect(() => {
    if (chart && isOpen) {
      setChartTitle(chart.title);
      setChartType(chart.type);
      setActiveTab(chart.queryType === "cube" ? 0 : 1);

      // Initialize axis labels from config
      if (chart.config?.xAxisLabel) {
        setXAxisLabel(chart.config.xAxisLabel as string);
      }
      if (chart.config?.yAxisLabel) {
        setYAxisLabel(chart.config.yAxisLabel as string);
      }
      // Initialize column labels for table charts
      if (chart.config?.columnLabels) {
        setColumnLabels(chart.config.columnLabels as Record<string, string>);
      }

      if (chart.queryType === "cube" && chart.cubeQuery) {
        setGuiQuery(chart.cubeQuery);
      } else if (chart.queryType === "sql" && chart.sqlQuery) {
        setSqlQuery(chart.sqlQuery.sql);
        // Auto-generate preview for SQL query - pass sql directly to avoid stale closure
        handleSQLPreviewWithQuery(chart.sqlQuery.sql);
      }
    }
  }, [chart, isOpen]);

  // Handle GUI Builder preview updates
  const handleGUIPreviewUpdate = (
    data: ChartDataUnion | null,
    type: ChartType,
    autoTitle: string,
  ) => {
    setPreviewData(data);
    setChartType(type);
    setGuiAutoTitle(autoTitle);
    setShowPreview(data !== null);
    setPreviewError(null);

    // Auto-fill title if empty
    if (!chartTitle && autoTitle) {
      setChartTitle(autoTitle);
    }
  };

  // Handle GUI Builder query updates
  const handleGUIQueryUpdate = (query: CubeQueryConfig | null) => {
    setGuiQuery(query);
  };

  // Handle SQL preview - accepts optional sql parameter for initialization
  const handleSQLPreviewWithQuery = async (sql: string) => {
    if (!sql.trim()) {
      setPreviewError("Please enter a SQL query");
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewError(null);

      // Transform SQL query to include fully qualified custom field names
      const transformedSQL = transformSQLQuery(sql);

      // Include preview date range in SQL query
      const query: SQLQueryConfig = {
        dateFrom: previewDateRange.from,
        dateTo: previewDateRange.to,
        sql: transformedSQL, // Use transformed SQL
      };
      const response: CubeQueryResponse = await executeSQLQuery(query);
      const transformed = transformCubeDataToChartFormat(
        response,
        chartType,
        null,
      );

      setPreviewData(transformed);
      setShowPreview(true);
    } catch (error) {
      console.error("Preview failed:", error);
      setPreviewError("Failed to generate preview. Please check your query.");
      setPreviewData(null);
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle SQL preview using current state
  const handleSQLPreview = () => handleSQLPreviewWithQuery(sqlQuery);

  const handleSave = async () => {
    if (!chartTitle.trim()) {
      showAlert({
        message: "Please enter a chart title",
        title: "Validation Error",
        type: "error",
      });
      return;
    }

    // Validation based on query type
    if (queryType === "cube") {
      if (!guiQuery) {
        showAlert({
          message: "Please configure your chart using the GUI Builder",
          title: "Validation Error",
          type: "error",
        });
        return;
      }
    } else {
      if (!sqlQuery.trim()) {
        showAlert({
          message: "Please enter a SQL query",
          title: "Validation Error",
          type: "error",
        });
        return;
      }
    }

    try {
      setLoading(true);

      // Calculate next Y position for new charts (place at bottom)
      const maxY = charts.reduce((max, c) => {
        const bottom = c.gridPosition.y + c.gridPosition.h;
        return Math.max(max, bottom);
      }, 0);

      const payload: CreateChartPayload = {
        gridPosition: isEditMode
          ? chart.gridPosition
          : {
              // Metric charts start smaller (2x2), others start at 4x6
              h: chartType === "metric" ? 2 : 4,
              w: chartType === "metric" ? 2 : 6,
              x: 0,
              y: maxY, // Place at bottom of existing charts
            },
        queryType,
        title: chartTitle,
        type: chartType,
      };

      if (queryType === "cube") {
        payload.cubeQuery = guiQuery!;
      } else {
        payload.sqlQuery = { sql: sqlQuery };
      }

      // Add config options if provided
      if (xAxisLabel || yAxisLabel || Object.keys(columnLabels).length > 0) {
        payload.config = {};
        if (xAxisLabel) {
          payload.config.xAxisLabel = xAxisLabel;
        }
        if (yAxisLabel) {
          payload.config.yAxisLabel = yAxisLabel;
        }
        if (Object.keys(columnLabels).length > 0) {
          payload.config.columnLabels = columnLabels;
        }
      }

      if (isEditMode) {
        await updateChart(dashboardId, chart.id, payload);
      } else {
        await addChart(dashboardId, payload);
      }

      // Reset form
      handleClose();

      // Notify parent
      onChartCreated();
    } catch (error) {
      console.error("Failed to create chart:", error);
      showAlert({
        message: "Failed to create chart. Please try again.",
        title: "Error",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setChartTitle("");
    setChartType("bar");
    setXAxisLabel("");
    setYAxisLabel("");
    setColumnLabels({});
    setActiveTab(0);
    setGuiQuery(null);
    setGuiAutoTitle("");
    setSqlQuery("");
    setPreviewData(null);
    setPreviewError(null);
    setShowPreview(false);
    setPreviewDateRange(dashboardDateRange);
    setShowDeleteModal(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!isEditMode || !chart) return;

    setIsDeleting(true);
    try {
      await deleteChart(dashboardId, chart.id);
      setShowDeleteModal(false);
      handleClose();
      onChartCreated(); // Refresh the dashboard
    } catch (error) {
      console.error("Failed to delete chart:", error);
      showAlert({
        message: "Failed to delete chart. Please try again.",
        title: "Error",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // SQL Query Builder
  const sqlQueryBuilder = (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SQL Query *
        </label>
        <div className="flex gap-4">
          <div className="flex-1">
            <SQLEditor
              value={sqlQuery}
              onChange={setSqlQuery}
              onRegisterInsert={handleRegisterInsert}
              placeholder="SELECT status, COUNT(*) as count FROM orders GROUP BY status"
            />
          </div>
          {/* Height matches SQLEditor: 10 rows × 24px line-height + 24px padding + 24px for helper text = ~288px */}
          <div className="w-64 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 h-[288px]">
            <SchemaReference onInsert={handleInsertText} />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Use Ctrl+Space for autocomplete. Click items in Schema Reference to
          insert.
        </p>
      </div>

      {/* Chart Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Chart Type *
        </label>

        {/* Common Chart Types */}
        <div className="mb-3">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
            Common Types
          </p>
          <div className="grid grid-cols-2 gap-2">
            {COMMON_SQL_CHART_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setChartType(type)}
                className={`
                  border-2 rounded-lg p-3 text-left transition
                  ${
                    chartType === type
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }
                `}
              >
                <p className="text-sm font-medium text-gray-900">
                  {CHART_TYPE_LABELS[type]}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Advanced Chart Types */}
        <details className="mt-3">
          <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
            See advanced chart types ({ADVANCED_SQL_CHART_TYPES.length})
          </summary>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {ADVANCED_SQL_CHART_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setChartType(type)}
                className={`
                  border-2 rounded-lg p-3 text-left transition
                  ${
                    chartType === type
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }
                `}
              >
                <p className="text-sm font-medium text-gray-900">
                  {CHART_TYPE_LABELS[type]}
                </p>
              </button>
            ))}
          </div>
        </details>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          SQL Query Guidelines
        </h4>
        <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
          <li>Query Cube.js semantic layer (e.g., SELECT * FROM orders)</li>
          <li>Use cube names as table names (orders, drivers, routes, etc.)</li>
          <li>For bar/column charts: First column = labels, others = values</li>
          <li>For pie/donut charts: Two columns (category, value)</li>
          <li>For metric charts: Single value result</li>
          <li>For tables: All columns will be displayed</li>
          <li>Workspace filtering is automatic</li>
        </ul>
      </div>

      {/* Preview Button */}
      <div className="border-t pt-4">
        <Button
          onClick={handleSQLPreview}
          disabled={previewLoading}
          color="light"
          className="w-full"
        >
          {previewLoading ? "Generating Preview..." : "Preview Chart"}
        </Button>
      </div>
    </div>
  );

  const previewPanel =
    showPreview || previewLoading || previewError ? (
      <div className="fixed inset-y-0 left-20 right-[672px] z-[52] bg-white border-r border-gray-200 flex flex-col">
        {/* Dimming overlay when modal is open */}
        {showDeleteModal && (
          <div className="absolute inset-0 bg-black/30 z-10 pointer-events-none" />
        )}
        {/* Header - outside scroll container so dropdown isn't clipped */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">Live Preview</h3>
          <DateRangeDropdown
            dateRange={{
              from: dateOnlyToLocalDate(previewDateRange.from),
              to: dateOnlyToLocalDate(previewDateRange.to),
            }}
            onChange={(range) => {
              setPreviewDateRange({
                from: dayjs(range.from).format("YYYY-MM-DD"),
                to: dayjs(range.to).format("YYYY-MM-DD"),
              });
            }}
          />
        </div>
        <div className="px-6 pb-6 overflow-y-auto flex-1">
          {/* Loading State */}
          {previewLoading && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">Generating preview...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {!previewLoading && previewError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{previewError}</p>
            </div>
          )}

          {/* Preview Chart */}
          {!previewLoading && !previewError && previewData && (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <ChartRenderer
                  chart={{
                    config:
                      xAxisLabel ||
                      yAxisLabel ||
                      Object.keys(columnLabels).length > 0
                        ? {
                            ...(Object.keys(columnLabels).length > 0 && {
                              columnLabels,
                            }),
                            ...(xAxisLabel && { xAxisLabel }),
                            ...(yAxisLabel && { yAxisLabel }),
                          }
                        : undefined,
                    createdAt: new Date().toISOString(),
                    cubeQuery: queryType === "cube" ? guiQuery! : undefined,
                    gridPosition: { h: 4, w: 6, x: 0, y: 0 },
                    id: "preview",
                    queryType,
                    sqlQuery:
                      queryType === "sql" ? { sql: sqlQuery } : undefined,
                    title: chartTitle || guiAutoTitle || "Preview",
                    type: chartType,
                    updatedAt: new Date().toISOString(),
                  }}
                  dateRange={previewDateRange}
                />
              </div>
            </>
          )}
        </div>
      </div>
    ) : undefined;

  return (
    <>
      {/* Chart Builder Drawer (Right Side) */}
      <Drawer
        isOpen={isOpen}
        onClose={handleClose}
        disableBackdrop={true}
        disableOutsideClick={true}
        leftContent={previewPanel}
        title={isEditMode ? "Edit Chart" : "Create Chart"}
        description={
          isEditMode
            ? "Update your chart configuration"
            : "Build a new chart for your dashboard"
        }
        icon="chartMixedLight"
        footer={
          <div className="border-t border-gray-200 p-4">
            <div className="flex justify-between">
              <div className="flex gap-3">
                <Button
                  onClick={handleSave}
                  disabled={loading || !chartTitle.trim()}
                >
                  {loading
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : isEditMode
                      ? "Update"
                      : "Create"}
                </Button>
                <Button type="button" plain onClick={handleClose}>
                  Cancel
                </Button>
              </div>
              {isEditMode && (
                <Button
                  color="red"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isDeleting}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-6 pb-6">
          {/* Chart Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chart Title *
            </label>
            <Input
              value={chartTitle}
              onChange={(value) => setChartTitle(value)}
              placeholder={guiAutoTitle || "e.g., Total Orders By Day"}
            />
          </div>

          {/* Query Builder Tabs */}
          <div>
            <TabGroup selectedIndex={activeTab} onChange={setActiveTab}>
              <TabList className="sticky flex border-b border-gray-200">
                <Tab className="focus:outline-none">
                  {({ selected }) => (
                    <div
                      className={clsx(
                        "text-sm inline-flex cursor-pointer border-b-2 px-4 py-2 font-semibold transition ease-in hover:text-gray-500 hover:border-gray-500",
                        selected && "border-gray-700 text-gray-700",
                        !selected && "border-transparent text-gray-400",
                      )}
                    >
                      GUI Builder
                    </div>
                  )}
                </Tab>
                <Tab className="focus:outline-none">
                  {({ selected }) => (
                    <div
                      className={clsx(
                        "text-sm inline-flex cursor-pointer border-b-2 px-4 py-2 font-semibold transition ease-in hover:text-gray-500 hover:border-gray-500",
                        selected && "border-gray-700 text-gray-700",
                        !selected && "border-transparent text-gray-400",
                      )}
                    >
                      SQL Query
                    </div>
                  )}
                </Tab>
              </TabList>

              <TabPanels className="mt-2 w-full">
                <TabPanel className="w-full">
                  <GUIBuilder
                    dashboardDateRange={previewDateRange}
                    initialQuery={chart?.cubeQuery || null}
                    initialType={chart?.type}
                    onPreviewUpdate={handleGUIPreviewUpdate}
                    onQueryUpdate={handleGUIQueryUpdate}
                  />
                </TabPanel>
                <TabPanel className="w-full">{sqlQueryBuilder}</TabPanel>
              </TabPanels>
            </TabGroup>
          </div>

          {/* Preview Error */}
          {previewError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{previewError}</p>
            </div>
          )}

          {/* Axis Labels - Only show for chart types with axes */}
          {CHART_TYPES_WITH_AXES.includes(chartType) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Axis Labels
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    X-Axis Label
                  </label>
                  <Input
                    value={xAxisLabel}
                    onChange={(value) => setXAxisLabel(value)}
                    placeholder="Leave empty for auto-generated"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Y-Axis Label
                  </label>
                  <Input
                    value={yAxisLabel}
                    onChange={(value) => setYAxisLabel(value)}
                    placeholder="Leave empty for auto-generated"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Column Labels - Only show for table charts with preview data */}
          {chartType === "table" &&
            previewData &&
            "columns" in previewData &&
            (previewData as TableData).columns?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Column Labels
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Customize how column headers appear in the table. Leave empty
                  to use auto-generated labels.
                </p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {(previewData as TableData).columns.map((column) => (
                    <div
                      key={column.key}
                      className="flex items-center gap-3 bg-gray-50 rounded-md p-2"
                    >
                      <div className="w-1/3 text-xs text-gray-500 truncate">
                        {column.key}
                      </div>
                      <div className="flex-1">
                        <Input
                          value={columnLabels[column.key] || ""}
                          onChange={(value) =>
                            setColumnLabels((prev) => ({
                              ...prev,
                              [column.key]: value,
                            }))
                          }
                          placeholder={column.label}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Slice Labels - Only show for pie/donut charts with preview data */}
          {(chartType === "pie" || chartType === "donut") &&
            previewData &&
            "labels" in previewData &&
            (previewData as BasicChartData).labels?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Slice Labels
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Customize how each slice is labeled in the chart. Leave empty
                  to use auto-generated labels.
                </p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {(previewData as BasicChartData).labels.map(
                    (label, index) => (
                      <div
                        key={`slice-${index}`}
                        className="flex items-center gap-3 bg-gray-50 rounded-md p-2"
                      >
                        <div className="w-1/3 text-xs text-gray-500 truncate">
                          {label}
                        </div>
                        <div className="flex-1">
                          <Input
                            value={columnLabels[`slice_${index}`] || ""}
                            onChange={(value) =>
                              setColumnLabels((prev) => ({
                                ...prev,
                                [`slice_${index}`]: value,
                              }))
                            }
                            placeholder={label}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
        </div>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Chart"
        subtitle="Are you sure you want to delete this chart? This action cannot be undone."
        width={400}
        zIndex={60}
      >
        <div className="mt-6 flex justify-end gap-3">
          <Button plain onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} isLoading={isDeleting}>
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}
